import React from 'react'
import PropTypes from 'prop-types'

import { makeStyles } from '@material-ui/core/styles'
import Button from '@material-ui/core/Button'
import TextField from '@material-ui/core/TextField'
import Dialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import CircularProgress from '@material-ui/core/CircularProgress'
import FormControl from '@material-ui/core/FormControl'
import InputLabel from '@material-ui/core/InputLabel'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import IconButton from '@material-ui/core/IconButton'
import Grid from '@material-ui/core/Grid'

import CancelIcon from '@material-ui/icons/Cancel'
import CheckCircleRoundedIcon from '@material-ui/icons/CheckCircleRounded'
import { DialogContentText, FormHelperText, Tooltip } from '@material-ui/core'

const useStyles = makeStyles((theme) => ({
  cancelButtonsGridWrapper: {
    paddingLeft: '0px',
    marginLeft: '-5px'
  }
}))

export default function BulkEditor (props) {
  const classes = useStyles()

  const [markupError, setMarkupError] = React.useState(false)
  const [markupErrorMessage, setMarkupErrorMessage] = React.useState('')
  const [overrideTypeError, setOverrideTypeError] = React.useState(false)
  const [overrideTypeErrorMessage, setOverrideTypeErrorMessage] = React.useState('')
  const [categoryId, setCategoryId] = React.useState(0)
  const [taxId, setTaxId] = React.useState(0)
  const [wholesalerFileId, setWholesalerFileId] = React.useState(0)
  const [overrideType, setOverrideType] = React.useState(0)
  const [markupValue, setMarkupValue] = React.useState('')
  const [restrictedAgeId, setRestrictedAgeId] = React.useState(0)
  const [applying, setApplying] = React.useState(false)

  React.useEffect(() => {
    // reset the form when the open status changes
    setApplying(false)
    setMarkupError(false)
    setMarkupErrorMessage('')
    setCategoryId(0)
    setTaxId(0)
    setWholesalerFileId(0)
    setMarkupValue('')
    setOverrideType(0)
    setRestrictedAgeId(0)
  }, [props.open])

  const onApply = (event) => {
    if (markupValue !== '') {
      if (overrideType === 0) {
        setOverrideTypeError(true)
        setOverrideTypeErrorMessage('You must select a Markup Type as well as Markup Value')

        return
      } else {
        setOverrideTypeError(false)
        setOverrideTypeErrorMessage('')
      }
    } else if (overrideType !== 0) {
      if (overrideType === -1) {
        // reset to default, so we don't mind what the markup value is
      } else {
        if (markupValue === '') {
          setMarkupError(true)
          setMarkupErrorMessage('You must enter a Markup Value when setting Markup Type')

          return
        } else {
          setMarkupError(false)
          setMarkupErrorMessage('')
        }
      }
    }

    setApplying(true)
    props.onApply({
      categoryId: categoryId,
      taxId: taxId,
      wholesalerFileId: wholesalerFileId,
      markupCalculationType: overrideType,
      markupValue: markupValue,
      restrictedAgeId: restrictedAgeId
    })
  }

  // category functions / events
  const onCategoryChange = (event) => {
    setCategoryId(event.target.value)
  }

  const getCategoryList = () => {
    const categories = []

    categories.push(<MenuItem key={0} value={0}>{'No change'}</MenuItem>)
    categories.push(<MenuItem key={-1} value={-1}>{'Revert to RDP Value'}</MenuItem>)

    for (const category of props.siteCategories) {
      categories.push(<MenuItem key={category.id} value={category.id}>{category.nodeListString}</MenuItem>)
    }

    return (categories)
  }

  const onResetCategory = () => {
    setCategoryId(0)
  }

  // tax rate functions / events
  const onTaxRateChange = (event) => {
    setTaxId(event.target.value)
  }

  const getTaxRateList = () => {
    const taxRates = []

    taxRates.push(<MenuItem key={0} value={0}>{'No change'}</MenuItem>)
    taxRates.push(<MenuItem key={-1} value={-1}>{'Revert to RDP Value'}</MenuItem>)

    for (const taxRate of props.taxRates) {
      taxRates.push(<MenuItem key={taxRate.id} value={taxRate.id}>{`${taxRate.taxRate}% - ${taxRate.description}`}</MenuItem>)
    }

    return (taxRates)
  }

  const onResetTaxRate = () => {
    setTaxId(0)
  }

  // wholesaler file functions / events
  const onWholesalerFileChange = (event) => {
    setWholesalerFileId(event.target.value)
  }

  const getWholesalerFileList = () => {
    const wholesalerFiles = []

    wholesalerFiles.push(<MenuItem key={0} value={0}>{'No change'}</MenuItem>)
    wholesalerFiles.push(<MenuItem key={-1} value={-1}>{'Revert to RDP Value'}</MenuItem>)

    for (const wholesalerFile of props.wholesalerFiles) {
      wholesalerFiles.push(<MenuItem key={wholesalerFile.id} value={wholesalerFile.id}>{wholesalerFile.description}</MenuItem>)
    }

    return (wholesalerFiles)
  }

  const onResetWholesalerFile = () => {
    setWholesalerFileId(0)
  }

  // override types functions / events
  const onOverrideTypeChange = (event) => {
    setOverrideType(event.target.value)

    setOverrideTypeError(false)
    setOverrideTypeErrorMessage('')
  }

  const getOverrideTypeList = () => {
    const overrideTypes = []

    overrideTypes.push(<MenuItem key={0} value={0}>{'No change'}</MenuItem>)
    overrideTypes.push(<MenuItem key={-1} value={-1}>{'Revert to RDP Value'}</MenuItem>)

    for (const overrideType of props.overrideTypes) {
      if (overrideType.value === 'Number') {
        continue
      }

      overrideTypes.push(<MenuItem key={overrideType.value} value={overrideType.value}>{overrideType.description}</MenuItem>)
    }

    return (overrideTypes)
  }

  // restricted age functions / events
  const onRestrictedAgeChange = (event) => {
    setRestrictedAgeId(event.target.value)
  }

  const getRestrictedAgeList = () => {
    const restrictedAges = []

    restrictedAges.push(<MenuItem key={0} value={0}>{'No change'}</MenuItem>)
    restrictedAges.push(<MenuItem key={-1} value={-1}>{'Revert to RDP Value'}</MenuItem>) // -1 is used for No Restriction

    for (const restrictedAge of props.ageRestrictions) {
      restrictedAges.push(<MenuItem key={restrictedAge.id} value={restrictedAge.id}>{restrictedAge.age + ''}</MenuItem>)
    }

    return (restrictedAges)
  }

  const onResetRestrictedAge = () => {
    setRestrictedAgeId(0)
  }

  const onMarkupChange = (event) => {
    setMarkupValue(event.target.value)

    if (event.target.value !== '') {
      // first of all - is the value a numeric one
      const numberTest = Number(event.target.value)

      if (isNaN(numberTest)) {
        setMarkupError(true)
        setMarkupErrorMessage('Markup value must be numeric')
      } else {
        setMarkupError(false)
        setMarkupErrorMessage('')
      }
    }
  }

  const onResetMarkup = () => {
    setOverrideType(0)
    setMarkupValue('')
  }

  // sort the data into suitable form to use
  const taxRates = getTaxRateList()
  const categories = getCategoryList()
  const wholesalerFiles = getWholesalerFileList()
  const overrideTypes = getOverrideTypeList()
  const restrictedAges = getRestrictedAgeList()

  const handleClose = (event, reason) => {
    // don't close by clicking background
    if (reason === 'backdropClick') {
      return
    }

    props.onClose(event, reason)
  }

  return (
    <Dialog maxWidth="sm" fullWidth={true} onClose={handleClose} open={props.open}>
      <DialogTitle>
        Bulk Edit
      </DialogTitle>
      <DialogContent dividers>
        <DialogContentText>
          Set the following attributes for {props.selectedProductCount} selected products:
        </DialogContentText>

        <Grid container spacing={2}>
          <Grid item xs={11}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryId}
                onChange={onCategoryChange}
              >
                {categories}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={1} className={classes.cancelButtonsGridWrapper}>
            <Tooltip arrow title="Reset category value">
              <span>
                <IconButton color="primary" disabled={categoryId === 0} onClick={onResetCategory}>
                  <CancelIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Grid>

          <Grid item xs={11}>
            <FormControl fullWidth>
              <InputLabel>Tax Rate</InputLabel>
              <Select
                value={taxId}
                onChange={onTaxRateChange}
              >
                {taxRates}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={1} className={classes.cancelButtonsGridWrapper}>
            <Tooltip arrow title="Reset tax rate value">
              <span>
                <IconButton color="primary" disabled={taxId === 0} onClick={onResetTaxRate}>
                  <CancelIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Grid>

          <Grid item xs={11}>
            <FormControl fullWidth>
              <InputLabel>Wholesaler File</InputLabel>
              <Select
                value={wholesalerFileId}
                onChange={onWholesalerFileChange}
              >
                {wholesalerFiles}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={1} className={classes.cancelButtonsGridWrapper}>
            <Tooltip arrow title="Reset wholesaler file value">
              <span>
                <IconButton color="primary" disabled={wholesalerFileId === 0} onClick={onResetWholesalerFile}>
                  <CancelIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Grid>

          <Grid item xs={11}>
            <FormControl fullWidth>
              <InputLabel>Age Restriction</InputLabel>
              <Select
                value={restrictedAgeId}
                onChange={onRestrictedAgeChange}
              >
                {restrictedAges}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={1} className={classes.cancelButtonsGridWrapper}>
            <Tooltip arrow title="Reset age restriction value">
              <span>
                <IconButton color="primary" disabled={restrictedAgeId === 0} onClick={onResetRestrictedAge}>
                  <CancelIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Grid>

          <Grid item xs={4}>
            <TextField
              type="text"
              label="Markup Value"
              InputLabelProps={{ shrink: true }}
              error={markupError}
              helperText={markupErrorMessage}
              fullWidth
              value={markupValue}
              onChange={onMarkupChange}
              placeholder={'No change'}
            />
          </Grid>

          <Grid item xs={7}>
            <FormControl fullWidth>
              <InputLabel error={overrideTypeError}>Markup Type</InputLabel>
              <Select
                value={overrideType}
                onChange={onOverrideTypeChange}
                error={overrideTypeError}
              >
                {overrideTypes}
              </Select>
              <FormHelperText error={overrideTypeError}>{overrideTypeErrorMessage}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={1} className={classes.cancelButtonsGridWrapper}>
            <Tooltip arrow title="Reset markup values">
              <span>
                <IconButton color="primary" disabled={overrideType === 0 && markupValue === ''} onClick={onResetMarkup}>
                  <CancelIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
        </Grid>

        <br />

        <DialogContentText>
          Note - only set the attributes you wish to change - the others will be ignored
        </DialogContentText>
        <DialogContentText>
          Markup changes will not be applied to user created products as they can only be Set Amount
        </DialogContentText>

      </DialogContent>
      <DialogActions>
        <Button autoFocus variant="contained" onClick={onApply} color="primary">
          {
            applying === true
              ? <><CircularProgress color="inherit" size={20} />&nbsp;&nbsp;</>
              : <><CheckCircleRoundedIcon />&nbsp;</>
          }
            Apply
        </Button>
        <Button variant="contained" onClick={props.onClose} color="secondary">
          <CancelIcon />&nbsp;Cancel
        </Button>
      </DialogActions>
    </Dialog>)
}

BulkEditor.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedProductCount: PropTypes.number.isRequired,
  siteCategories: PropTypes.array.isRequired,
  ageRestrictions: PropTypes.array.isRequired,
  taxRates: PropTypes.array.isRequired,
  wholesalerFiles: PropTypes.array.isRequired,
  overrideTypes: PropTypes.array.isRequired,
  onApply: PropTypes.func.isRequired
}
